import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Lock, RefreshCw, Phone, MapPin, Clock, ShoppingBag, CalendarDays, ArrowLeft, ImageIcon, Search, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";
import { Input } from "./ui/input";
import { UploadBox } from "./UploadBox";
import { PriceEditor } from "./PriceEditor";
import { TextEditor } from "./TextEditor";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GALLERY_SLOTS = ["gallery-1", "gallery-2", "gallery-3", "gallery-4", "gallery-5", "gallery-6", "gallery-7"];
const ABOUT_SLOTS = ["about-1", "about-2", "about-3", "about-4"];

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-ES", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

// Small cleanup toolbar for the reservations & orders lists. Lets the admin
// bulk-delete completed / cancelled items so the DB doesn't accumulate old rows.
const BulkCleanup = ({ type, onBulk }) => {
  const label = type === "orders" ? "pedidos" : "reservas";
  return (
    <div
      data-testid={`bulk-cleanup-${type}`}
      className="mb-4 border border-q5 bg-[#141414] px-4 py-3 flex flex-wrap items-center gap-2"
    >
      <p className="overline text-[10px] mr-2">Limpiar {label}</p>
      <button
        type="button"
        data-testid={`bulk-${type}-done`}
        onClick={() => onBulk(type, "done", `todos los ${label} completados`)}
        className="text-[11px] uppercase tracking-[0.15em] px-3 py-1.5 border border-neutral-500/40 text-neutral-300 hover:border-gold hover:text-gold transition-colors flex items-center gap-1.5"
      >
        <Trash2 size={11} /> Completados
      </button>
      <button
        type="button"
        data-testid={`bulk-${type}-cancelled`}
        onClick={() => onBulk(type, "cancelled", `todos los ${label} cancelados`)}
        className="text-[11px] uppercase tracking-[0.15em] px-3 py-1.5 border border-neutral-500/40 text-neutral-300 hover:border-gold hover:text-gold transition-colors flex items-center gap-1.5"
      >
        <Trash2 size={11} /> Cancelados
      </button>
      <button
        type="button"
        data-testid={`bulk-${type}-both`}
        onClick={() => onBulk(type, "done_and_cancelled", `todos los ${label} completados y cancelados`)}
        className="text-[11px] uppercase tracking-[0.15em] px-3 py-1.5 border border-red-500/40 text-red-300 hover:bg-red-500/10 transition-colors flex items-center gap-1.5"
      >
        <Trash2 size={11} /> Completados + Cancelados
      </button>
      <button
        type="button"
        data-testid={`bulk-${type}-all`}
        onClick={() => onBulk(type, "all", `ABSOLUTAMENTE TODOS los ${label} (incluidos activos)`)}
        className="ml-auto text-[11px] uppercase tracking-[0.15em] px-3 py-1.5 border border-red-600 bg-red-600/10 text-red-300 hover:bg-red-600/30 hover:text-white transition-colors flex items-center gap-1.5"
      >
        <Trash2 size={11} /> Borrar todo
      </button>
    </div>
  );
};



const StatusBadge = ({ status }) => {
  const map = {
    pending: { label: "Pendiente", cls: "bg-yellow-500/20 text-yellow-300 border-yellow-500/40" },
    received: { label: "Recibido", cls: "bg-blue-500/20 text-blue-300 border-blue-500/40" },
    confirmed: { label: "Confirmado", cls: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" },
    sent: { label: "Enviado", cls: "bg-indigo-500/20 text-indigo-300 border-indigo-500/40" },
    cancelled: { label: "Cancelado", cls: "bg-red-500/20 text-red-300 border-red-500/40" },
    done: { label: "Completado", cls: "bg-neutral-500/20 text-neutral-300 border-neutral-500/40" },
  };
  const s = map[status] || { label: status, cls: "bg-neutral-500/20 text-neutral-300 border-neutral-500/40" };
  return <span className={`px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] border ${s.cls}`}>{s.label}</span>;
};

const Admin = () => {
  const [key, setKey] = useState(() => localStorage.getItem("q5_admin_key") || "");
  const [authed, setAuthed] = useState(false);
  const [data, setData] = useState({ reservations: [], orders: [] });
  const [tab, setTab] = useState("reservations");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [menuItems, setMenuItems] = useState([]);
  const [imgSlots, setImgSlots] = useState([]);
  const [dishQuery, setDishQuery] = useState("");
  const [dishCat, setDishCat] = useState("all");

  const load = useCallback(async (k) => {
    if (!k) return;
    setLoading(true);
    setError("");
    try {
      const r = await axios.get(`${API}/admin/data`, { params: { key: k } });
      setData(r.data);
      setAuthed(true);
      localStorage.setItem("q5_admin_key", k);
    } catch (e) {
      setError(e?.response?.status === 401 ? "Código incorrecto" : "Error de conexión");
      setAuthed(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (key) load(key);
  }, [key, load]);

  useEffect(() => {
    if (!authed) return;
    axios.get(`${API}/menu`).then((r) => setMenuItems(r.data.items || [])).catch(() => {});
    axios.get(`${API}/images`).then((r) => setImgSlots(r.data.slots || [])).catch(() => {});
  }, [authed]);

  const onImageChanged = (slot, present) => {
    setImgSlots((prev) => present ? Array.from(new Set([...prev, slot])) : prev.filter((s) => s !== slot));
  };

  useEffect(() => {
    if (!authed) return;
    const t = setInterval(() => load(key), 30000);
    return () => clearInterval(t);
  }, [authed, key, load]);

  const updateStatus = async (type, id, status) => {
    try {
      await axios.post(`${API}/admin/${type}/${id}/status`, null, { params: { key, status } });
      await load(key);
    } catch (e) {
      // ignore
      console.error(e);
    }
  };

  const deleteOne = async (type, id) => {
    if (!window.confirm(`¿Borrar ${type === "orders" ? "este pedido" : "esta reserva"}? Esta acción no se puede deshacer.`)) return;
    try {
      await axios.delete(`${API}/admin/${type}/${id}`, { params: { key } });
      await load(key);
    } catch (e) {
      console.error(e);
    }
  };

  const bulkDelete = async (type, scope, label) => {
    if (!window.confirm(`¿Seguro que quieres borrar ${label}? Esta acción no se puede deshacer.`)) return;
    try {
      const r = await axios.post(`${API}/admin/${type}/bulk-delete`, null, { params: { key, scope } });
      await load(key);
      window.alert(`Borrados: ${r.data.deleted}`);
    } catch (e) {
      console.error(e);
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-6">
        <form
          data-testid="admin-login-form"
          onSubmit={(e) => { e.preventDefault(); load(key); }}
          className="bg-[#141414] border border-q5 w-full max-w-md p-8"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 border border-gold flex items-center justify-center"><Lock size={16} className="text-gold" /></div>
            <div>
              <p className="overline">Panel · Q5</p>
              <h1 className="font-serif-q5 text-2xl text-white">Acceso restaurante</h1>
            </div>
          </div>
          <p className="text-sm text-neutral-400 mb-6">Introduce el código de acceso para ver las reservas y pedidos.</p>
          <Input
            data-testid="admin-key-input"
            type="password"
            placeholder="Código de acceso"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            className="bg-transparent border-q5 h-12 focus-visible:ring-gold focus-visible:ring-offset-0 rounded-sm"
          />
          {error && <p data-testid="admin-error" className="text-red-400 text-xs mt-3">{error}</p>}
          <button data-testid="admin-login-btn" type="submit" disabled={loading} className="btn-primary-q5 w-full mt-5">
            {loading ? "..." : "Entrar"}
          </button>
          <Link to="/" className="block text-center text-xs text-neutral-500 hover:text-gold mt-5 uppercase tracking-[0.2em]">
            ← Volver a la web
          </Link>
        </form>
      </div>
    );
  }

  const { reservations, orders } = data;
  const pendingRes = reservations.filter((r) => r.status === "pending").length;
  const pendingOrd = orders.filter((o) => o.status === "received").length;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <header className="border-b border-q5 px-6 py-5 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-4">
          <Link to="/" data-testid="admin-back-link" className="text-neutral-400 hover:text-gold flex items-center gap-1.5 text-xs uppercase tracking-[0.2em]">
            <ArrowLeft size={14} /> Web
          </Link>
          <span className="font-serif-q5 text-2xl text-gold">Q5</span>
          <span className="text-xs uppercase tracking-[0.25em] text-neutral-400">Panel del restaurante</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="admin-refresh-btn"
            onClick={() => load(key)}
            className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-neutral-300 hover:text-gold border border-q5 hover:border-gold px-3 py-2"
          >
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refrescar
          </button>
          <button
            data-testid="admin-logout-btn"
            onClick={() => { localStorage.removeItem("q5_admin_key"); setKey(""); setAuthed(false); }}
            className="text-xs uppercase tracking-[0.18em] text-neutral-500 hover:text-red-400 px-2"
          >
            Salir
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid sm:grid-cols-3 gap-4 mb-8">
          <button
            data-testid="tab-reservations"
            onClick={() => setTab("reservations")}
            className={`p-5 border text-left transition-colors ${tab === "reservations" ? "border-gold bg-[#141414]" : "border-q5 hover:border-gold/60"}`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-gold"><CalendarDays size={18} /> <span className="overline">Reservas</span></div>
              {pendingRes > 0 && <span className="bg-[#A91D11] text-white text-[10px] px-2 py-0.5 rounded-full">{pendingRes} nuevas</span>}
            </div>
            <p className="font-serif-q5 text-4xl text-white mt-2">{reservations.length}</p>
          </button>
          <button
            data-testid="tab-orders"
            onClick={() => setTab("orders")}
            className={`p-5 border text-left transition-colors ${tab === "orders" ? "border-gold bg-[#141414]" : "border-q5 hover:border-gold/60"}`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-gold"><ShoppingBag size={18} /> <span className="overline">Pedidos online</span></div>
              {pendingOrd > 0 && <span className="bg-[#A91D11] text-white text-[10px] px-2 py-0.5 rounded-full">{pendingOrd} nuevos</span>}
            </div>
            <p className="font-serif-q5 text-4xl text-white mt-2">{orders.length}</p>
          </button>
          <button
            data-testid="tab-images"
            onClick={() => setTab("images")}
            className={`p-5 border text-left transition-colors ${tab === "images" ? "border-gold bg-[#141414]" : "border-q5 hover:border-gold/60"}`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-gold"><ImageIcon size={18} /> <span className="overline">Fotos de la web</span></div>
            </div>
            <p className="font-serif-q5 text-4xl text-white mt-2">{imgSlots.length}</p>
            <p className="text-[10px] uppercase tracking-[0.15em] text-neutral-500 mt-1">fotos subidas</p>
          </button>
        </div>

        {tab === "reservations" && (
          <div data-testid="reservations-list" className="space-y-3">
            <BulkCleanup type="reservations" onBulk={bulkDelete} />
            {reservations.length === 0 && <p className="text-neutral-500 text-center py-12">Sin reservas todavía.</p>}
            {reservations.map((r) => (
              <article key={r.id} data-testid={`res-row-${r.id}`} className="bg-[#141414] border border-q5 p-5 grid md:grid-cols-12 gap-3 items-center">
                <div className="md:col-span-3">
                  <p className="font-serif-q5 text-xl text-white">{r.name}</p>
                  <a href={`tel:${r.phone}`} className="text-gold text-sm flex items-center gap-1.5 mt-1 hover:underline"><Phone size={12} /> {r.phone}</a>
                  {r.email && <p className="text-neutral-500 text-xs mt-0.5">{r.email}</p>}
                </div>
                <div className="md:col-span-3">
                  <p className="overline">Fecha · Hora</p>
                  <p className="text-white mt-1 flex items-center gap-1.5"><CalendarDays size={13} className="text-gold" /> {r.date} · {r.time}</p>
                  <p className="text-neutral-400 text-sm mt-0.5">{r.guests} {r.guests === 1 ? "comensal" : "comensales"}</p>
                </div>
                <div className="md:col-span-3">
                  <p className="overline">Notas</p>
                  <p className="text-neutral-300 text-sm mt-1">{r.notes || "—"}</p>
                  <p className="text-neutral-600 text-[10px] mt-2 flex items-center gap-1"><Clock size={10} /> {formatDate(r.created_at)}</p>
                </div>
                <div className="md:col-span-3 flex flex-col md:items-end gap-2">
                  <StatusBadge status={r.status} />
                  <div className="flex flex-wrap md:justify-end gap-1.5">
                    {r.status !== "confirmed" && r.status !== "cancelled" && (
                      <button data-testid={`res-confirm-${r.id}`} onClick={() => updateStatus("reservations", r.id, "confirmed")} className="px-4 py-2 text-[11px] uppercase tracking-[0.18em] font-medium bg-emerald-600 hover:bg-emerald-500 text-white border border-emerald-500 transition-colors flex items-center gap-2">✓ Confirmar reserva</button>
                    )}
                    {r.status !== "cancelled" && (
                      <button data-testid={`res-cancel-${r.id}`} onClick={() => updateStatus("reservations", r.id, "cancelled")} className="px-3 py-2 text-[11px] uppercase tracking-[0.18em] border border-red-500/40 text-red-300 hover:bg-red-500/10">Cancelar</button>
                    )}
                    <button
                      data-testid={`res-delete-${r.id}`}
                      onClick={() => deleteOne("reservations", r.id)}
                      aria-label="Borrar reserva"
                      className="p-2 text-[11px] border border-red-600/50 text-red-400 hover:bg-red-600/20 hover:text-white transition-colors"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}

        {tab === "orders" && (
          <div data-testid="orders-list" className="space-y-3">
            <BulkCleanup type="orders" onBulk={bulkDelete} />
            {orders.length === 0 && <p className="text-neutral-500 text-center py-12">Sin pedidos todavía.</p>}
            {orders.map((o) => (
              <article key={o.id} data-testid={`order-row-${o.id}`} className="bg-[#141414] border border-q5 p-5">
                <div className="grid md:grid-cols-12 gap-3 items-start">
                  <div className="md:col-span-3">
                    <p className="font-serif-q5 text-xl text-white">{o.customer_name}</p>
                    <a href={`tel:${o.phone}`} className="text-gold text-sm flex items-center gap-1.5 mt-1 hover:underline"><Phone size={12} /> {o.phone}</a>
                    {o.email && <p className="text-neutral-500 text-xs mt-0.5">{o.email}</p>}
                  </div>
                  <div className="md:col-span-3">
                    <p className="overline">{o.type === "delivery" ? "A domicilio" : "Recogida en local"}</p>
                    {o.address && <p className="text-white mt-1 text-sm flex items-start gap-1.5"><MapPin size={13} className="text-gold mt-0.5 flex-shrink-0" /> {o.address}</p>}
                    {o.notes && <p className="text-neutral-400 text-xs mt-1">Notas: {o.notes}</p>}
                    <p className="text-neutral-600 text-[10px] mt-2 flex items-center gap-1"><Clock size={10} /> {formatDate(o.created_at)}</p>
                  </div>
                  <div className="md:col-span-4">
                    <p className="overline mb-2">Pedido</p>
                    <ul className="space-y-1">
                      {o.items.map((it, i) => (
                        <li key={i} className="text-sm flex justify-between gap-3 text-neutral-300">
                          <span><span className="text-gold mr-1">{it.quantity}×</span>{it.name}</span>
                          <span className="text-neutral-500">{(it.price * it.quantity).toFixed(2)} €</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="md:col-span-2 flex flex-col items-end gap-2">
                    <p className="font-serif-q5 text-2xl text-gold">{o.total.toFixed(2)} €</p>
                    <StatusBadge status={o.status} />
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-q5 flex flex-wrap items-center gap-2">
                  {o.status !== "confirmed" && o.status !== "sent" && o.status !== "done" && (
                    <button
                      data-testid={`order-confirm-${o.id}`}
                      onClick={() => updateStatus("orders", o.id, "confirmed")}
                      className="flex-1 sm:flex-initial sm:min-w-[200px] px-5 py-2.5 text-xs uppercase tracking-[0.18em] font-medium bg-emerald-600 hover:bg-emerald-500 text-white border border-emerald-500 transition-colors flex items-center justify-center gap-2"
                    >
                      ✓ Confirmar pedido
                    </button>
                  )}
                  {o.status === "confirmed" && o.type === "delivery" && (
                    <button
                      data-testid={`order-send-${o.id}`}
                      onClick={() => updateStatus("orders", o.id, "sent")}
                      className="flex-1 sm:flex-initial sm:min-w-[200px] px-5 py-2.5 text-xs uppercase tracking-[0.18em] font-medium bg-indigo-600 hover:bg-indigo-500 text-white border border-indigo-500 transition-colors flex items-center justify-center gap-2"
                    >
                      → Enviar pedido
                    </button>
                  )}
                  {(o.status === "confirmed" || o.status === "sent") && (
                    <button
                      data-testid={`order-done-${o.id}`}
                      onClick={() => updateStatus("orders", o.id, "done")}
                      className="flex-1 sm:flex-initial sm:min-w-[200px] px-5 py-2.5 text-xs uppercase tracking-[0.18em] font-medium bg-gold/20 hover:bg-gold/30 text-gold border border-gold transition-colors flex items-center justify-center gap-2"
                    >
                      ✓ Marcar como completado
                    </button>
                  )}
                  {o.status !== "cancelled" && o.status !== "done" && (
                    <button
                      data-testid={`order-cancel-${o.id}`}
                      onClick={() => updateStatus("orders", o.id, "cancelled")}
                      className="px-4 py-2.5 text-xs uppercase tracking-[0.18em] border border-red-500/40 text-red-300 hover:bg-red-500/10 transition-colors"
                    >
                      Cancelar
                    </button>
                  )}
                  <button
                    data-testid={`order-delete-${o.id}`}
                    onClick={() => deleteOne("orders", o.id)}
                    aria-label="Borrar pedido"
                    className="ml-auto p-2.5 text-xs border border-red-600/50 text-red-400 hover:bg-red-600/20 hover:text-white transition-colors flex items-center gap-1.5"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}

        {tab === "images" && (
          <div data-testid="images-panel" className="space-y-12">
            {/* PORTADA */}
            <div>
              <h3 className="font-serif-q5 text-2xl text-white mb-1">Portada</h3>
              <p className="text-sm text-neutral-500 mb-5">Banner principal del local. Si no subes ninguna, se muestra el vídeo por defecto.</p>
              <div className="max-w-sm">
                <UploadBox slot="hero" label="Imagen de portada / banner" adminKey={key} present={imgSlots.includes("hero")} onChanged={onImageChanged} aspect="aspect-video" />
              </div>
            </div>

            {/* SOBRE NOSOTROS */}
            <div>
              <h3 className="font-serif-q5 text-2xl text-white mb-1">Sobre nosotros</h3>
              <p className="text-sm text-neutral-500 mb-5">Cuatro fotos del bloque que aparece justo debajo de la portada.</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {ABOUT_SLOTS.map((slot, i) => (
                  <UploadBox key={slot} slot={slot} label={`Sobre nosotros ${i + 1}`} adminKey={key} present={imgSlots.includes(slot)} onChanged={onImageChanged} />
                ))}
              </div>
            </div>

            {/* GALERÍA */}
            <div>
              <h3 className="font-serif-q5 text-2xl text-white mb-1">Galería</h3>
              <p className="text-sm text-neutral-500 mb-5">Hasta 7 fotos del local, ambiente o platos destacados.</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {GALLERY_SLOTS.map((slot, i) => (
                  <UploadBox key={slot} slot={slot} label={`Galería ${i + 1}`} adminKey={key} present={imgSlots.includes(slot)} onChanged={onImageChanged} />
                ))}
              </div>
            </div>

            {/* PLATOS */}
            <div>
              <h3 className="font-serif-q5 text-2xl text-white mb-1">Fotos de los platos</h3>
              <p className="text-sm text-neutral-500 mb-5">Sube una foto real para cada plato. Los que no tengan foto muestran un recuadro elegante con el logo.</p>

              <div className="flex flex-col sm:flex-row gap-3 mb-6">
                <div className="relative flex-1 max-w-sm">
                  <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gold pointer-events-none" />
                  <Input
                    data-testid="dish-search"
                    placeholder="Buscar plato..."
                    value={dishQuery}
                    onChange={(e) => setDishQuery(e.target.value)}
                    className="bg-transparent border-q5 h-10 pl-9 focus-visible:ring-gold focus-visible:ring-offset-0 rounded-sm text-white"
                  />
                </div>
                <select
                  data-testid="dish-cat-filter"
                  value={dishCat}
                  onChange={(e) => setDishCat(e.target.value)}
                  className="bg-[#141414] border border-q5 h-10 px-3 text-sm text-neutral-300 rounded-sm focus:outline-none focus:border-gold"
                >
                  <option value="all">Todas las categorías</option>
                  <option value="entrantes">Entrantes & Verdura</option>
                  <option value="ramen">Ramen</option>
                  <option value="arroz">Arroz</option>
                  <option value="tallarines">Tallarines</option>
                  <option value="carnes">Carnes</option>
                  <option value="marisco">Marisco</option>
                  <option value="sushi">Sushi</option>
                  <option value="poke">Poke & Sashimi</option>
                  <option value="refrescos">Refrescos & Aguas</option>
                  <option value="cervezas">Cervezas & Sake</option>
                  <option value="vinos">Vinos (botella)</option>
                  <option value="copas_vino">Copas de Vino</option>
                  <option value="cafes">Cafés & Infusiones</option>
                  <option value="cubatas">Combinados</option>
                  <option value="chupitos">Chupitos</option>
                  <option value="cocteles">Cócteles</option>
                  <option value="postres">Postres</option>
                </select>
                <span className="text-xs text-neutral-500 self-center">
                  {imgSlots.filter((s) => s.startsWith("dish-")).length} / {menuItems.length} platos con foto
                </span>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {menuItems
                  .filter((it) => dishCat === "all" || it.category === dishCat)
                  .filter((it) => {
                    const q = dishQuery.trim();
                    if (!q) return true;
                    const norm = (s) => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
                    return norm(it.name_es).includes(norm(q));
                  })
                  .map((it) => (
                    <div key={it.id} className="flex flex-col gap-2">
                      <UploadBox
                        slot={`dish-${it.id}`}
                        label={`Nº ${it.number} · ${it.name_es}`}
                        adminKey={key}
                        present={imgSlots.includes(`dish-${it.id}`)}
                        onChanged={onImageChanged}
                      />
                      <PriceEditor
                        itemId={it.id}
                        currentPrice={it.price}
                        adminKey={key}
                        onChanged={() => load(key)}
                      />
                      <TextEditor
                        itemId={it.id}
                        item={it}
                        adminKey={key}
                        onChanged={() => load(key)}
                      />
                    </div>
                  ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;
